import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useQuery, skipToken } from "@tanstack/react-query";
import { User } from "../../../types";
import { getUserProfile, useSlideInMenu } from "../hooks/useSlideInMenu";
import { Avatar } from "../../user/Avatar";
import { SlideInHeader } from "../components/SlideInHeader";
import { SLIDE_IN_MENUS } from "../../../screens/root/constants";
import { useTranslation } from "react-i18next";
import { UserTags } from "./UserTags";
import { MyPagesList } from "./MyPagesList";
import { UserVitals } from "./UserVitals";
import { useEntityCache } from "../../../hooks/useEntityCache";

interface UserProfileProps {
  onCloseMenu?: () => void;
}

export function UserProfile({ onCloseMenu }: UserProfileProps) {
  const { t } = useTranslation();
  const { saveEntityToCache } = useEntityCache();
  const { username, navigationContext, goBack } = useSlideInMenu();

  const { data: fetchedUserResponse, isLoading } = useQuery({
    enabled: !!username,
    queryKey: ["userProfileUser", username],
    queryFn: async () => {
      const response = await getUserProfile(username ?? "", navigationContext);
      const user = response?.data.payload.results;

      if (user) {
        saveEntityToCache(user);
      }
      return response;
    },
    networkMode: "offlineFirst",
  });

  const fetchedUser = fetchedUserResponse?.data.payload.results;
  const cacheKey = `${fetchedUser?.entity_type}-${fetchedUser?.id}`;
  const { data: user } = useQuery<User>({
    enabled: !!fetchedUser,
    queryKey: [cacheKey],
    queryFn: skipToken,
    networkMode: "offlineFirst",
  });

  if (!user || isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        grow={1}
        pt="16px"
        height="100%"
      >
        <Spinner color="blaze.blaze" />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" height="100%">
      <SlideInHeader
        openMenu={SLIDE_IN_MENUS.userProfile}
        onCloseMenu={onCloseMenu}
        handleBack={goBack}
      />
      <Flex
        className="styled-scrollbar"
        flexDirection="column"
        flexGrow={1}
        overflowY="auto"
        padding="24px"
        gap="16px"
        alignItems="center"
        width="full"
      >
        <UserProfileHeader user={user} />
        <Box height="1px" background="transparent.white.20" width="88px" />
        <Text
          fontSize="14px"
          fontWeight="400"
          lineHeight="130%"
          fontStyle="regular"
          color="neutral.500"
          textAlign="center"
        >
          {`${t("components.slideInMenu.userProfile.memberSince")} ${user.join_date.split("-")[0]}`}{" "}
          • {user.country.name}
        </Text>
        <Box height="1px" background="transparent.white.88" />
        <UserVitals user={user} />
        {user.artist_users.length > 0 && (
          <>
            <Box
              height="1px"
              background="transparent.white.20"
              paddingY="8px"
            />
            <MyPagesList user={user} />
          </>
        )}
      </Flex>
    </Flex>
  );
}

function UserProfileHeader({ user }: { user: User }) {
  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      gap="16px"
      pt="24px"
      px="24px"
    >
      <Avatar
        isGold={user.gold}
        username={user.name}
        imageSrc={user.avatar_url}
        customSize={120}
        ringStroke="4px"
      />
      <UserTags user={user} />
      <Flex flexDirection="column" alignItems="center" gap="4px">
        <Text
          fontSize="20px"
          fontWeight="700"
          lineHeight="26px"
          fontStyle="bold"
          color="white"
          textAlign="center"
        >
          {user.full_name || `@${user.name}`}
        </Text>
        {user.full_name && (
          <Text
            fontSize="14px"
            fontWeight="700"
            lineHeight="130%"
            fontStyle="bold"
            color="neutral.200"
            textAlign="center"
          >
            @{user.name}
          </Text>
        )}
        <Text
          fontSize="14px"
          fontWeight="400"
          lineHeight="130%"
          fontStyle="regular"
          color="neutral.300"
          textAlign="center"
        >
          {user.description}
        </Text>
      </Flex>
    </Flex>
  );
}
